import { useCallback } from 'react';
import { ArcElement, RightClickEventArgs } from '@thive/canvas';
import { Vector2 } from 'three';

import { isIntersect } from '@/modules/common/helpers/array';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FDDM_AREA_TAGS, FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { ContextMenuServiceFactory } from '@/modules/canvas/services/ContextMenu/ContextMenuServiceFactory';
import { CONTEXT_MENU_CONTROL_POINT_DETECTION_RADIUS } from '../constants/elements';
import { CONTEXT_MENU_OFFSET_MULTIPLIER } from '@/modules/contextMenu/constants';

export const useContextMenu = () => {
  const findClosestPointIndex = (
    points: Vector2[],
    targetPoint: Vector2,
    maxDistance: number | undefined = undefined,
  ): number | undefined => {
    if (points.length === 0) return undefined;

    let currentClosestDistance = points[0].distanceTo(targetPoint);

    const closestPoint = points.reduce((result, p) => {
      const distance = p.distanceTo(targetPoint);

      if (distance < currentClosestDistance) {
        currentClosestDistance = distance;
        return p;
      }

      return result;
    }, points[0]);

    if (maxDistance !== undefined && currentClosestDistance > maxDistance) return undefined;
    return points.findIndex((p) => p.equals(closestPoint));
  };

  const canvasRightClicked = useCallback((e: RightClickEventArgs) => {
    const { selectedIds, mode } = useAppStore.getState();

    if (mode !== 'EDITABLE') return;

    const element = e.elements[0];
    const contextMenuSvc = ContextMenuServiceFactory.getInstance().createContextMenuService();

    // Bubble arcs can be shadowed by area elements when the area is hit first.
    // Always prefer a bubble Arc element over whatever came first.
    const bubbleElement = e.elements.find(
      (el): el is ArcElement =>
        el.type === 'Arc' && !!el.tags?.includes(FDDM_TAG.BUBBLE) && !!el.radius && !!el.parentId,
    );

    // bubble — checked before area tags so it is never shadowed
    if (bubbleElement) {
      const radius = bubbleElement.radius ?? 0;
      const xOffset = radius > 0 ? (1 / radius) * CONTEXT_MENU_OFFSET_MULTIPLIER : 0;
      const position =
        xOffset > 0 ? e.elementPosition.add(new Vector2(xOffset, 0)) : e.elementPosition;

      contextMenuSvc.setRelatedComponentId(bubbleElement.parentId);
      contextMenuSvc.setContext('CONNECTION');
      contextMenuSvc.setPosition(position);
      contextMenuSvc.setActiveConnectionTab(
        bubbleElement.tags?.includes(FDDM_TAG.A_TO_B_CONNECTION)
          ? 'aToBConnections'
          : 'bToAConnections',
      );
      return;
    }

    contextMenuSvc.setRelatedComponentId(undefined);
    contextMenuSvc.setRelatedControlPoint(undefined);
    contextMenuSvc.setActiveConnectionTab(undefined);

    let relatedAreaIds: string[] = [];

    if (selectedIds.length > 0) {
      relatedAreaIds = selectedIds;
    } else if (element?.tags && isIntersect(element.tags, FDDM_AREA_TAGS)) {
      relatedAreaIds = [element.id];
    }

    contextMenuSvc.setRelatedAreaIds(relatedAreaIds);

    // flow stop
    if (element?.tags?.includes(FDDM_TAG.FLOW_STOP)) {
      contextMenuSvc.setRelatedComponentId(element.id);
      contextMenuSvc.setContext('FLOW_STOP');
      contextMenuSvc.setPosition(e.elementPosition);
      return;
    }

    // highway control point
    if (
      element?.tags?.includes(FDDM_TAG.HIGHWAY_AREA) &&
      element.type === 'LinePath' &&
      element.points
    ) {
      const pointIndex = findClosestPointIndex(
        element.points.map((point) => new Vector2(point.x, point.y)),
        e.position,
        CONTEXT_MENU_CONTROL_POINT_DETECTION_RADIUS,
      );

      if (pointIndex !== undefined) {
        contextMenuSvc.setRelatedControlPoint({ elementId: element.id, pointIndex });
        contextMenuSvc.setContext('CONTROL_POINT');
        contextMenuSvc.setPosition(e.elementPosition);
        return;
      }
    }

    const isDistConOrCrossingConnectionElement =
      element?.parentId &&
      (element.tags?.includes(FDDM_TAG.DISTANT_CONNECTION) ||
        element.tags?.includes(FDDM_TAG.CROSSING));

    if (isDistConOrCrossingConnectionElement) {
      contextMenuSvc.setRelatedComponentId(element.parentId);
      contextMenuSvc.setContext('CONNECTION');

      const isDistcon = element.tags?.includes(FDDM_TAG.DISTANT_CONNECTION);
      const xOffset =
        isDistcon && element && 'radius' in element && element.radius
          ? (1 / element.radius) * CONTEXT_MENU_OFFSET_MULTIPLIER
          : 0;
      const position =
        xOffset > 0 ? e.elementPosition.add(new Vector2(xOffset, 0)) : e.elementPosition;

      contextMenuSvc.setPosition(position);
      return;
    }

    contextMenuSvc.setContext('AREA');
    contextMenuSvc.setPosition(e.elementPosition);
  }, []);
  return { canvasRightClicked };
};
